import { useState } from "react";
import { FlaskConical, Loader2, Send } from "lucide-react";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { Agent } from "@/types/agent/agent";

interface ExperimentalCardProps {
  agent: Agent;
  brand?: string;
}

interface AskGenieResponse {
  genie_id: string;
  user_id: string;
  response: string;
}

const EXAMPLE_PROMPTS = [
  "What are your business hours?",
  "Can you summarize what you help customers with?",
  "What should I do if I have a billing question?",
];

export const ExperimentalCard = ({ agent, brand = "#4E9CFF" }: ExperimentalCardProps) => {
  const [prompt, setPrompt] = useState("");
  const [isAsking, setIsAsking] = useState(false);
  const [result, setResult] = useState<AskGenieResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const askGenie = async (text: string) => {
    if (!text.trim() || isAsking) return;
    setIsAsking(true);
    setError(null);
    setResult(null);

    const { data, error: invokeError } = await supabase.functions.invoke("elevenlabs", {
      body: {
        resource: "conversations",
        action: "ask-text",
        params: { genie_id: agent.id, prompt: text, mode: "wait" },
      },
    });

    if (invokeError) {
      setError(invokeError.message || "Failed to ask genie");
    } else if (data?.error) {
      setError(data.error);
    } else {
      setResult(data as AskGenieResponse);
    }

    setIsAsking(false);
  };

  return (
    <OverviewSectionCard
      title="Experimental"
      subtitle="Internal tools for testing upcoming features — visible to admins only"
      genieIcon="flask"
      brand={brand}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-purple-600" />
          <h3 className="text-base font-semibold text-gray-900">Ask Genie (text-only)</h3>
        </div>
        <p className="text-sm text-gray-600">
          Sends a one-off text prompt straight to this genie's ElevenLabs agent and returns its reply. Used to test
          the ask-genie integration ahead of the HG Mail sync feature.
        </p>

        <div className="flex flex-wrap gap-2">
          {EXAMPLE_PROMPTS.map((example) => (
            <button
              key={example}
              type="button"
              onClick={() => {
                setPrompt(example);
                askGenie(example);
              }}
              disabled={isAsking}
              className="px-3 py-1.5 text-sm rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {example}
            </button>
          ))}
        </div>

        <div className="space-y-2">
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Type a custom prompt to ask this genie..."
            rows={3}
            className="resize-none"
          />
          <Button
            onClick={() => askGenie(prompt)}
            disabled={isAsking || !prompt.trim()}
            className="gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold"
          >
            {isAsking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {isAsking ? "Asking genie..." : "Ask genie"}
          </Button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        )}

        {result && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 space-y-1.5">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Response</div>
            <p className="text-sm text-gray-900 whitespace-pre-wrap">{result.response}</p>
            <div className="text-xs text-gray-400 pt-1">
              genie_id: {result.genie_id} · user_id: {result.user_id}
            </div>
          </div>
        )}
      </div>
    </OverviewSectionCard>
  );
};
